<template>
  <div>
    <v-card class="card">
      <v-tabs v-model="tab" class="align-center" density="default" height="64">
        <v-tab ripple value="list-enterprise-customers">Enterprise Customers</v-tab>
        <v-tab ripple value="add-enterprise-customer">Add Enterprise Customer</v-tab>
        <v-spacer></v-spacer>
        <v-text-field
          class="align-self-right mt-0 pt-0"
          append-inner-icon="mdi-magnify"
          v-model="search"
          label="Search"
          :single-line="true"
          hide-details
          variant="outlined"
          density="compact"
          v-if="tab === 'list-enterprise-customers'"
        >
        </v-text-field>
      </v-tabs>

      <v-window v-if="tab === 'list-enterprise-customers'">
        <v-data-table
          :loading="loadingEnterpriseCustomers"
          v-if="enterpriseCustomers"
          :headers="headersSubscriptions"
          :items="filteredEnterpriseCustomers"
          v-model:sort-by="sortBy"
          hover
        >
          <template v-slot:item.payerUserEmail="{ item }">
            <div>
              <v-tooltip v-if="item.payerUser?.id" location="bottom">
                <template v-slot:activator="{ props: props }">
                  <span class="font-weight-bold" v-bind="props">{{ item.payerUser?.email || item.payerUserEmail }}</span>
                </template>
                <span>{{ item.payerUser?.id }}</span>
              </v-tooltip>
              <span v-else class="font-weight-bold">{{ item.payerUserEmail }}</span>
            </div>
          </template>

          <template v-slot:item.startDate="{ item }">
            <div>
              {{ item.startDate ? formatDate(item.startDate) : 'N/A' }}
            </div>
          </template>

          <template v-slot:item.expirationDate="{ item }">
            <div :class="{'text-red': new Date().getTime()>new Date(item.expirationDate).getTime()}">
              {{ formatDate(item.expirationDate) }}
            </div>
          </template>

          <template v-slot:item.payerUserName="{ item }">
            <div>
              <v-tooltip v-if="item.payerUser?.id" location="bottom">
                <template v-slot:activator="{ props: props }">
                  <span class="font-weight-bold" v-bind="props">{{ item.payerUser?.firstname }} {{
                    item.payerUser?.lastname
                  }}</span>
                </template>
                <span>{{ item.payerUser?.id }}</span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:item.createdAt="{ item }">
            <div>
              {{ formatDate(item.createdAt) }}
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-menu
              location="bottom right"
              :offset="[8, 0]"
            >
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  variant="text"
                  icon="mdi-dots-vertical"
                />
              </template>

              <v-list density="compact">
                <v-list-item @click="editableCustomer = item" prepend-icon="mdi-pencil">
                  <v-list-item-title>Edit</v-list-item-title>
                </v-list-item>
                <v-list-item @click="addEnterpriseMemberDialogData = item" prepend-icon="mdi-plus"
                             :disabled="!canAddMemberForCustomer(item)">
                  <v-list-item-title>Add member</v-list-item-title>
                </v-list-item>
                <v-tooltip location="bottom" :disabled="additionalMemberCount(item) > 0">
                  <template v-slot:activator="{ props }">
                    <div v-bind="props">
                      <v-list-item
                        @click="changePayerCustomer = item"
                        prepend-icon="mdi-account-switch"
                        :disabled="additionalMemberCount(item) === 0"
                      >
                        <v-list-item-title>Change payer</v-list-item-title>
                      </v-list-item>
                    </div>
                  </template>
                  <span>Add another member before you can change the payer.</span>
                </v-tooltip>
                <v-tooltip location="bottom" :disabled="additionalMemberCount(item) === 0">
                  <template v-slot:activator="{ props }">
                    <div v-bind="props">
                      <v-list-item
                        @click="deletableCustomer = item"
                        prepend-icon="mdi-delete"
                        base-color="red"
                        :disabled="additionalMemberCount(item) > 0"
                      >
                        <v-list-item-title>Delete</v-list-item-title>
                      </v-list-item>
                    </div>
                  </template>
                  <span>Remove all members before deleting this enterprise customer.</span>
                </v-tooltip>
              </v-list>
            </v-menu>
          </template>

          <template v-slot:no-data>
            <v-alert v-if="normalizedSearch" color="error" icon="mdi-alert">
              Your search for "{{ normalizedSearch }}" found no results.
            </v-alert>
            <div v-else>
              No data available
            </div>
          </template>
        </v-data-table>
      </v-window>
      <v-window v-else-if="tab === 'add-enterprise-customer'">
        <admin-add-enterprise-customer @onDone="tab = 'list-enterprise-customers'"/>
      </v-window>
    </v-card>

    <edit-enterprise-user-dialog
      :is-visible="!!editableCustomer"
      :customer="editableCustomer"
      :used-seat-count="editableCustomer ? usedSeatCount(editableCustomer) : 0"
      :is-loading="loadingUpdateEnterpriseCustomer"
      @cancel="editableCustomer = null"
      @save="updateEnterpriseCustomer"
    />

    <admin-add-enterprise-member-dialog
      :is-visible="!!addEnterpriseMemberDialogData"
      :customer="addEnterpriseMemberDialogData"
      :is-loading="loadingAddEnterpriseMember"
      @cancel="addEnterpriseMemberDialogData = null"
      @save="addEnterpriseMember"
    />

    <delete-confirmation-dialog
      v-if="deletableCustomer"
      :model-value="!!deletableCustomer"
      title="Delete enterprise customer"
      :text="deleteDialogText"
      :confirm-text="deletableCustomer.company"
      :on-delete="confirmDeleteEnterpriseCustomer"
      @update:model-value="(value) => { if (!value) { deletableCustomer = null } }"
    />

    <change-enterprise-payer-dialog
      :is-visible="!!changePayerCustomer"
      :customer="changePayerCustomer"
      :member-emails="changePayerCustomer ? memberEmailsForCustomer(changePayerCustomer) : []"
      :is-loading="loadingChangeEnterprisePayer"
      @cancel="changePayerCustomer = null"
      @save="changeEnterprisePayer"
    />

    <info-message-dialog
      v-if="infoMessageDialogData"
      :is-open="infoMessageDialogData.isOpen"
      :title="infoMessageDialogData.title"
      :message="infoMessageDialogData.message"
      @close="infoMessageDialogData = null"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import format from 'date-fns/format'
import EditEnterpriseUserDialog from '@/components/EditEnterpriseUserDialog.vue'
import AdminAddEnterpriseCustomer from '@/components/AdminAddEnterpriseCustomer.vue'
import AdminAddEnterpriseMemberDialog from '@/components/AdminAddEnterpriseMemberDialog.vue'
import InfoMessageDialog from '@/components/InfoMessageDialog.vue'
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue'
import ChangeEnterprisePayerDialog from '@/components/ChangeEnterprisePayerDialog.vue'

export default {
  components: {
    AdminAddEnterpriseCustomer,
    EditEnterpriseUserDialog,
    AdminAddEnterpriseMemberDialog,
    InfoMessageDialog,
    DeleteConfirmationDialog,
    ChangeEnterprisePayerDialog
  },
  data: () => ({
    tab: 'list-enterprise-customers',
    sortBy: [{ key: 'registrationDate', order: 'desc' }],
    dialog: [],
    search: '',
    expanded: [],
    headersSubscriptions: [
      {
        title: 'Company',
        sortable: true,
        key: 'company'
      },
      {
        title: 'Payer user email',
        sortable: false,
        key: 'payerUserEmail'
      },
      {
        title: 'Payer user name',
        sortable: false,
        key: 'payerUserName'
      },
      {
        title: 'Start Date',
        sortable: true,
        key: 'startDate'
      },
      {
        title: 'Expiration Date',
        sortable: true,
        key: 'expirationDate'
      },
      {
        title: 'Quantity',
        sortable: true,
        key: 'quantity'
      },
      {
        title: 'Created At',
        key: 'createdAt'
      },
      {
        title: 'Actions',
        key: 'actions',
        sortable: false
      }
    ],
    editableCustomer: null,
    addEnterpriseMemberDialogData: null,
    deletableCustomer: null,
    changePayerCustomer: null,
    infoMessageDialogData: null
  }),
  computed: {
    ...mapState({
      subscriptions: state => state.subscriptions.subscriptions,
      enterpriseCustomers: state => state.enterpriseCustomers.enterpriseCustomers,
      loadingEnterpriseCustomers: state => state.enterpriseCustomers.loadingEnterpriseCustomers,
      loadingAddEnterpriseMember: state => state.enterpriseCustomers.loadingAddEnterpriseMember,
      loadingUpdateEnterpriseCustomer: state => state.enterpriseCustomers.loadingUpdateEnterpriseCustomer,
      loadingChangeEnterprisePayer: state => state.enterpriseCustomers.loadingChangeEnterprisePayer
    }),
    groupedSubscriptions () {
      const grouped = (this.subscriptions || []).reduce((acc, subscription) => {
        if (!subscription.payerUser) {
          return acc
        }
        const payerId = subscription.payerUser.id

        if (!acc[payerId]) {
          acc[payerId] = {
            id: subscription.payerUser.id,
            payerUser: subscription.payerUser,
            email: subscription.email,
            children: []
          }
        }

        if (subscription.email !== subscription.payerUser.email) {
          acc[payerId].children.push({ email: subscription.email })
        }

        return acc
      }, {})

      return Object.values(grouped)
    },
    memberEmailsByCustomerId () {
      const byEnterpriseCustomerId = {}
      const byPayerUserId = {}

      for (const sub of this.subscriptions || []) {
        if (!sub.email) {
          continue
        }
        const email = sub.email.toLowerCase()
        if (sub.enterpriseCustomerId) {
          if (!byEnterpriseCustomerId[sub.enterpriseCustomerId]) {
            byEnterpriseCustomerId[sub.enterpriseCustomerId] = []
          }
          byEnterpriseCustomerId[sub.enterpriseCustomerId].push(email)
        }
        if (sub.payerUser?.id) {
          if (!byPayerUserId[sub.payerUser.id]) {
            byPayerUserId[sub.payerUser.id] = []
          }
          byPayerUserId[sub.payerUser.id].push(email)
        }
      }

      return { byEnterpriseCustomerId, byPayerUserId }
    },
    normalizedSearch () {
      return this.search.trim()
    },
    filteredEnterpriseCustomers () {
      const query = this.normalizedSearch.toLowerCase()
      if (!query) {
        return this.enterpriseCustomers || []
      }

      const { byEnterpriseCustomerId, byPayerUserId } = this.memberEmailsByCustomerId

      return (this.enterpriseCustomers || []).filter((customer) => {
        if ((customer.company || '').toLowerCase().includes(query)) {
          return true
        }

        const payer = customer.payerUser || {}
        const firstname = (payer.firstname || '').toLowerCase()
        const lastname = (payer.lastname || '').toLowerCase()
        const fullName = `${firstname} ${lastname}`.trim()
        const payerEmail = (payer.email || customer.payerUserEmail || '').toLowerCase()

        if (payerEmail.includes(query)) {
          return true
        }
        if (firstname.includes(query) || lastname.includes(query) || fullName.includes(query)) {
          return true
        }

        const fromEnterpriseCustomer = byEnterpriseCustomerId[customer.id] || []
        if (fromEnterpriseCustomer.some((email) => email.includes(query))) {
          return true
        }

        const fromPayer = customer.payerUser?.id ? (byPayerUserId[customer.payerUser.id] || []) : []

        return fromPayer.some((email) => email.includes(query))
      })
    },
    deleteDialogText () {
      if (!this.deletableCustomer) {
        return ''
      }
      const company = this.deletableCustomer.company
      const payerEmail = this.deletableCustomer.payerUser?.email || this.deletableCustomer.payerUserEmail

      return `This permanently deletes the enterprise subscription for "${company}". The payer (${payerEmail}) will lose access and be asked to choose a new plan. This action cannot be undone.`
    }
  },
  methods: {
    canAddMemberForCustomer (customer) {
      if (!customer.payerUser) {
        const memberCount = (this.subscriptions || []).filter(
          (sub) => sub.enterpriseCustomerId === customer.id
        ).length
        return memberCount < customer.quantity
      }
      const members = this.groupedSubscriptions.find((subscription) => subscription.id === customer.payerUser.id)
      return (members?.children?.length || 0) + 1 < customer.quantity
    },
    allMemberEmailsForCustomer (customer) {
      const { byEnterpriseCustomerId, byPayerUserId } = this.memberEmailsByCustomerId

      return new Set([
        ...(byEnterpriseCustomerId[customer.id] || []),
        ...(customer.payerUser?.id ? (byPayerUserId[customer.payerUser.id] || []) : [])
      ])
    },
    usedSeatCount (customer) {
      return this.allMemberEmailsForCustomer(customer).size
    },
    memberEmailsForCustomer (customer) {
      const emails = this.allMemberEmailsForCustomer(customer)
      const payerEmail = (customer.payerUser?.email || customer.payerUserEmail || '').toLowerCase()
      emails.delete(payerEmail)

      return Array.from(emails)
    },
    additionalMemberCount (customer) {
      return this.memberEmailsForCustomer(customer).length
    },
    async confirmDeleteEnterpriseCustomer () {
      const customer = this.deletableCustomer
      if (!customer) {
        return
      }
      const response = await this.$store.dispatch('enterpriseCustomers/removeEnterpriseCustomer', { enterpriseCustomerId: customer.id })
      if (response?.success) {
        this.$store.commit('snackbar/showMessage', { content: 'Enterprise customer has been deleted.' }, { root: true })
        this.$store.dispatch('subscriptions/listSubscriptions')
      } else {
        this.infoMessageDialogData = {
          message: response?.message || 'Failed to delete enterprise customer.',
          isOpen: true
        }
      }
    },
    async changeEnterprisePayer (payload) {
      const response = await this.$store.dispatch('enterpriseCustomers/changeEnterprisePayer', payload)
      if (response?.success) {
        this.$store.commit('snackbar/showMessage', { content: 'Payer has been changed.' }, { root: true })
        this.$store.dispatch('subscriptions/listSubscriptions')
        this.changePayerCustomer = null
      } else if (response) {
        this.infoMessageDialogData = {
          message: response.message || 'Failed to change payer.',
          isOpen: true
        }
      }
    },
    formatDate (date) {
      return format(new Date(date), 'MMMM d, yyyy')
    },
    updateEnterpriseCustomer (data) {
      const { id, ...input } = data
      this.$store.dispatch('enterpriseCustomers/updateEnterpriseCustomer', { enterpriseUserId: id, input })

      this.editableCustomer = null
    },
    async addEnterpriseMember (data) {
      const { id, email, enterpriseCustomerId } = data

      const newEnterpriseMember = { email }
      if (id) {
        newEnterpriseMember.payerUserId = id
      }
      if (enterpriseCustomerId) {
        newEnterpriseMember.enterpriseCustomerId = enterpriseCustomerId
      }

      const response = await this.$store.dispatch('enterpriseCustomers/addEnterpriseMember', newEnterpriseMember)
      if (response?.success) {
        this.$store.commit('snackbar/showMessage', { content: 'Enterprise member has been added successfully.' }, { root: true })
        this.$store.dispatch('subscriptions/listSubscriptions')
      } else {
        this.infoMessageDialogData = {
          message: response.message,
          isOpen: true
        }
      }

      this.addEnterpriseMemberDialogData = null
    }
  },
  mounted () {
    this.$store.dispatch('subscriptions/listSubscriptions')
    this.$store.dispatch('enterpriseCustomers/listEnterpriseCustomers')
  }
}
</script>

<style scoped>
.card {
  border-radius: 12px;
  background: var(--light-1);
  border: 1px solid var(--light-1);
  padding: 12px 16px 16px !important;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
}
</style>
